import { Injectable, inject } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { from, lastValueFrom, Observable, of, timer } from "rxjs";
import { catchError, exhaustMap, finalize, map, takeWhile, tap } from "rxjs/operators";
import { TranslateService } from "@ngx-translate/core";

import { SharedApiService } from "./api/sharedApi.service";
import { SharedService } from "./shared.service";
import {
  IExecuteProcedureResult,
  ResultCode,
  GetGdaHeaderRequest,
  IGetGdaHeaderResponseData,
  IGetGdaHeaderRequestData,
} from "./model/pos.api.model";
import { ProcessingDocumentDialogComponent } from "src/app/components/processing-document-dialog/processing-document-dialog.component";

@Injectable({
  providedIn: "root",
})
export class HeaderProcessingService {
  private readonly _sharedApiService = inject(SharedApiService);
  private readonly _sharedService = inject(SharedService);
  private readonly _dialog = inject(MatDialog);
  private readonly _translate = inject(TranslateService);
  private readonly _maxAttempts = 60;

  public async monitorHeader(
    headerUid: string
  ): Promise<IExecuteProcedureResult<IGetGdaHeaderResponseData> | null> {
    if (!headerUid) {
      return null;
    }

    const dialogRef = this._dialog.open(ProcessingDocumentDialogComponent, {
      disableClose: true,
    });

    const result = await lastValueFrom(this._pollHeader(headerUid));
    await dialogRef.close();

    // Check if polling timed out (no successful result)
    if (!result || !this._isProcessingComplete(result)) {
      await this._sharedService.throwErrorDialog(
        this._translate.instant("webPos.HeaderProcessing.TimeoutMessage")
      );
    }

    return result;
  }

  private _pollHeader(
    headerUid: string
  ): Observable<IExecuteProcedureResult<IGetGdaHeaderResponseData> | null> {
    const request = new GetGdaHeaderRequest(headerUid);

    let lastResult: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null = null;

    return timer(0, 1000).pipe(
      exhaustMap(() =>
        from(
          this._sharedApiService.executeProcedure<
            IGetGdaHeaderRequestData,
            IExecuteProcedureResult<IGetGdaHeaderResponseData>
          >(request)
        ).pipe(
          catchError((error) => {
            console.error("Header processing poll failed", error);
            return of<IExecuteProcedureResult<IGetGdaHeaderResponseData>>(null);
          })
        )
      ),
      tap((result: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null) => {
        if (result?.data) {
          lastResult = result;
        }
      }),
      takeWhile(
        (result: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null, index) =>
          !this._isProcessingComplete(result) && index < this._maxAttempts - 1,
        true
      ),
      map((): IExecuteProcedureResult<IGetGdaHeaderResponseData> | null => lastResult),
      finalize(() => {
        // ensure spinner/dialog cleanup happens in monitorHeader
      })
    );
  }

  private _isProcessingComplete(
    result: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null
  ): boolean {
    if (!result || result.result !== ResultCode.OK || !result.data) {
      return false;
    }

    const payload = Array.isArray(result.data) ? result.data[0] : result.data;

    if (!payload) {
      return false;
    }

    return !!payload.HeaderID;
  }

  public getHeaderIdFromResult(
    result: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null
  ): number | null {
    if (!result?.data) {
      return null;
    }

    const payload = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!payload) {
      return null;
    }

    const headerIdCandidates = [
      payload.HeaderID,
      payload.headerID,
      payload.id,
      payload.HeaderId,
      payload.headerId,
    ];

    const headerId = headerIdCandidates.find(
      (value) => typeof value === 'number'
    ) as number | undefined;

    return headerId ?? null;
  }

  public getDocumentNumberFromResult(
    result: IExecuteProcedureResult<IGetGdaHeaderResponseData> | null
  ): string | null {
    if (!result?.data) {
      return null;
    }

    const payload = Array.isArray(result.data) ? result.data[0] : result.data;
    if (!payload) {
      return null;
    }

    return payload.DocumentNumber ?? null;
  }
}
